import React from 'react';
import styles from './StructuredModuleRenderer.module.scss';
import type { WebappStructuredContentModule } from '@eventbrite/event-listing-service-client';
import classNames from 'classnames';
import { LazyImage } from './components/LazyImage';
import { LazyVideo } from './components/LazyVideo';

type ModuleType = WebappStructuredContentModule['type'];

export const MODULE_TYPES: Record<Uppercase<ModuleType>, ModuleType> = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
} as const;

export const TEXT_ALIGNMENTS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

type TextContentProps = {
  text: string;
  alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS];
};

type ImageContentProps = {
  url: string;
  altText?: string;
  captionText?: string;
  width?: number;
  height?: number;
};

type VideoContentProps = {
  url: string;
  altText?: string;
};

const getTextAlignment = (alignment: (typeof TEXT_ALIGNMENTS)[keyof typeof TEXT_ALIGNMENTS]) => {
  switch (alignment) {
    case TEXT_ALIGNMENTS.LEFT:
      return styles.textLeft;
    case TEXT_ALIGNMENTS.CENTER:
      return styles.textCenter;
    case TEXT_ALIGNMENTS.RIGHT:
      return styles.textRight;
  }
};

const TextContent = ({ text, alignment }: TextContentProps) => (
  <div className={getTextAlignment(alignment)} dangerouslySetInnerHTML={{ __html: text }} />
);

const VideoContent = ({ url, altText }: VideoContentProps) => (
  <div className={styles.video}>
    <LazyVideo src={url} altText={altText} />
  </div>
);

const ImageContent = ({ url, altText, captionText, width = 0, height = 0 }: ImageContentProps) => (
  <div className={styles.imageContent}>
    {width > 0 && height > 0 ? (
      <LazyImage
        src={url}
        alt={altText || ''}
        width={width}
        height={height}
        style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
      />
    ) : (
      <LazyImage
        src={url}
        alt={altText || ''}
        fill
        style={{ position: 'relative', height: 'auto', width: '100%' }}
        objectFit="contain"
      />
    )}
    {captionText && <span className={styles.caption}>{captionText}</span>}
  </div>
);

export type StructuredModuleRendererProps = {
  module: WebappStructuredContentModule;
};

export const StructuredModuleRenderer = ({ module }: StructuredModuleRendererProps) => {
  const className = classNames(styles.structuredContent, styles[module.type]);

  switch (module.type) {
    case MODULE_TYPES.TEXT:
      const textContent = module as TextContentProps;
      return (
        <div className={className} data-testid="text-content">
          <TextContent text={textContent.text} alignment={textContent.alignment} />
        </div>
      );

    case MODULE_TYPES.IMAGE:
      const imageContent = module as ImageContentProps;
      return (
        <div className={className} data-testid="image-content">
          <ImageContent
            url={imageContent.url}
            altText={imageContent.altText}
            captionText={imageContent.captionText}
            width={imageContent.width}
            height={imageContent.height}
          />
        </div>
      );
    case MODULE_TYPES.VIDEO:
      const videoContent = module as VideoContentProps;
      return (
        <div className={className} data-testid="video-content">
          <VideoContent url={videoContent.url} altText={videoContent.altText} />
        </div>
      );
    default:
      return <></>;
  }
};
